import { format, parseISO } from 'date-fns';
import type { ReactNode } from 'react';
import type { SupplierUploadTracking } from '../../../lib/api/barratt';
import { cn } from '../../../lib/utils';
import { formatOtif } from '../uploadCycle';

interface UploadedFactsProps {
  row: SupplierUploadTracking;
  className?: string;
}

/** One figure: the number carries the weight, the word explains it. */
function Fact({ value, children }: { value: ReactNode; children: ReactNode }) {
  return (
    <span className="whitespace-nowrap">
      <span className="font-medium text-foreground">{value}</span>{' '}
      <span className="text-muted-foreground">{children}</span>
    </span>
  );
}

/**
 * What arrived for an obligation that has been met — row count, the outcome
 * figure for its dataset, and the file it came from.
 */
export default function UploadedFacts({ row, className }: UploadedFactsProps) {
  return (
    <div className={cn('flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm', className)}>
      <Fact value={row.records_count.toLocaleString()}>row{row.records_count === 1 ? '' : 's'}</Fact>
      {row.otif_pct !== null && (
        <span className="whitespace-nowrap text-muted-foreground">
          {'OTIF '}
          <span className="font-medium text-foreground">{formatOtif(row.otif_pct)}</span>
        </span>
      )}
      {row.ncr_count !== null && (
        <Fact value={row.ncr_count.toLocaleString()}>NCR{row.ncr_count === 1 ? '' : 's'}</Fact>
      )}
      {row.uploaded_at && (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {format(parseISO(row.uploaded_at), 'd MMM')}
        </span>
      )}
      {row.file_name && (
        <span className="min-w-0 max-w-full truncate text-xs text-muted-foreground" title={row.file_name}>
          {row.file_name}
        </span>
      )}
    </div>
  );
}
